<template>
  <div class="integration-con">
    <div class="title">Import CSV Files</div>
    
    <div class="directoryTable scrollable-55vh" style="height: auto !important">
      <table class="table">
        <thead class="sticky-header">
          <tr>
            <th class="icon-col">Type</th>
            <th>File Name</th>
            <th>Preview</th>
            <th>Status</th>
            <th class="icon-col"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in tableData" :key="item.name">
            <td>
              {{ item.title }}
            </td>
            <td style="width: 50%">
              <div
                :class="[
                  'file-select-con',
                  {
                    'drag-over': state.dragOver.includes(item.name),
                    'loading-upload': state.loading
                  }
                ]"
                @drop="(event) => onDrop(event, item as any)"
                @dragover="(event) => onDragOver(event, item as any)"
                @dragleave="state.dragOver = state.dragOver.filter((i) => i !== item.name)"
                tabindex="1"
              >
                <template v-if="item.added">
                  {{ item.added.file.name }}
                </template>
                <template v-else>
                  <Typography color="primary700" weight="600" size="1.15rem">
                    Drag and Drop Files Here
                  </Typography>

                  <typography>or</typography>
                  <v-btn
                    :disabled="state.loading"
                    color="primary"
                    density="compact"
                    size="large"
                    @click="(event) => event.currentTarget.querySelector('input').click()"
                    >Browse Files
                    <input
                      accept="text/csv"
                      type="file"
                      style="display: none"
                      v-on:change="addFile(($event.currentTarget as any).files[0], item as any)"
                    />
                  </v-btn>
                </template>
              </div>
            </td>

            <td>
              <v-btn
                v-if="item.validate"
                icon
                color="normal"
                style="height: 35px; width: 35px"
                @click="
                  state.preview = {
                    str: item.added?.str || '',
                    validation: item.validate,
                    type: item as any
                  }
                "
                ><v-icon>mdi-file-find</v-icon></v-btn
              >
            </td>
            <td>
              <template v-if="item.loading">
                <v-progress-circular indeterminate size="small"></v-progress-circular>
              </template>
              <template v-if="item.validate">
                <!-- <div :class="`${item.validate.valid ? 'success' : 'error'}-status`">
                  {{ item.validate.valid ? "Valid" : "Invalid" }}
                </div> -->
                <div :class="['validation-status', { invalid: !item.validate.valid }]" tabindex="1">
                  <v-tooltip v-if="!item.validate.valid" location="bottom" activator="parent">
                    Open Preview to see invalid messages
                  </v-tooltip>
                  <v-icon :color="item.validate.valid ? 'rgb(4, 231, 4)' : 'rgb(255, 0, 0)'">{{
                    item.validate.valid ? "mdi-check-decagram" : "mdi-close-circle"
                  }}</v-icon>
                  <span>
                    {{ item.validate.valid ? "File is Valid" : "File is Invalid" }}
                  </span>
                </div>
              </template>
              <template v-if="!item.added">
                <v-btn color="primary" variant="outlined" @click="downloadPreview(item as any)">
                  Download Preview
                </v-btn>
              </template>
            </td>
            <td>
              <v-btn
                v-if="item.added"
                @click="state.added = state.added.filter((i) => i !== item.added)"
                icon
                color="normal"
                style="height: 35px; width: 35px"
                ><v-icon color="primary">mdi-trash-can-outline</v-icon></v-btn
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="d-flex justify-end mt-4 w-100" v-if="state.added.length">
      <v-btn :loading="state.loading" @click="upload">Upload</v-btn>
    </div>
  </div>
  <CsvPreviewModal
    :show="!!state.preview"
    @update:show="state.preview = null"
    :file-str="state.preview?.str || ''"
    :validation="state.preview?.validation || ({} as any)"
    :csv-type="state.preview?.type||({} as any)"
  ></CsvPreviewModal>
  <div class="mt-4"></div>
</template>

<script setup lang="ts">
import Typography from "@/components/typography.vue";
import { useAlertsStore } from "@/stores/alerts";
import { useSchoolStore } from "@/stores/school";
import downloadFile from "@/utils";
import { computed } from "vue";
import { reactive } from "vue";
import CsvPreviewModal from "./csvPreviewModal.vue";
import { validateCsv, type TCsvType, csvTypes, integrationTypes } from "./utils";
import FetchService from "@/services/fetch.service";
import type { OtherIntegration } from "@/api_types/school";

const schoolStore = useSchoolStore();
let switch1 = true;

const props = defineProps<{
  integration: (typeof integrationTypes)[number]["value"];
  data?: OtherIntegration;
  loadingData?: boolean;
}>();
const state = reactive({
  selectedName: csvTypes[0].name,
  dragOver: [] as string[],
  loading: false,
  added: [] as {
    file: File;
    filename: string;
    str: string | null;
    name: string;
  }[],
  preview: null as null | {
    str: string;
    validation: ReturnType<typeof validateCsv>;
    type: TCsvType;
  }
});

const addFile = async (file: File, item: TCsvType) => {
  if (state.loading) return;
  state.dragOver = state.dragOver.filter((i) => i !== item.name);
  if (file?.type == "text/csv") {
    const exists = state.added.find((i) => i.name === item.name);
    if (exists) {
      exists.file = file;
    } else {
      const obj = {
        file,
        filename: `${item.fileName}-${schoolStore.school_id}.csv`,
        str: null,
        name: item.name
      };
      state.added.push(obj);
    }
    const str = await file.text();
    const obj = state.added.find((i) => i.name === item.name);
    if (obj) obj.str = str || "";
  } else {
    if (file) useAlertsStore().addAlert("error", "File upload only supports csv files", 7);
  }
};

const onDrop = (event, item: TCsvType | Readonly<TCsvType>) => {
  if (state.loading) return;
  event.preventDefault();
  addFile(event.dataTransfer?.files[0], item);
};

const onDragOver = (event, item: TCsvType) => {
  if (state.loading) return;
  if (!state.dragOver.includes(item.name)) {
    state.dragOver.push(item.name);
  }
  event.preventDefault();
};

const tableData = computed(() => {
  if (props.integration === "none") {
    return csvTypes
      .filter((i) => i.name === "teachers")
      .map((item) => {
        const added = state.added.find((i) => i.name == item.name);
        return Object.assign(item, {
          added,
          loading: added?.str === null,
          validate: added?.str ? validateCsv(added.str, item as any) : null
        });
      });  
  }
  if (props.integration === 'nycdoe'){
    return csvTypes
      .filter((i) => (i.name === "parents" || i.name === "parent_student"))
      .map((item) => {
        const added = state.added.find((i) => i.name == item.name);
        return Object.assign(item, {
          added,
          loading: added?.str === null,
          validate: added?.str ? validateCsv(added.str, item as any) : null
        });
      });
  }
  if (props.integration === 'hybrid'){
    return csvTypes
      .filter((i) => (i.name != 'parents' && i.name != 'parent_student'))
      .map((item) => {
        const added = state.added.find((i) => i.name == item.name);
        return Object.assign(item, {
          added,
          loading: added?.str === null,
          validate: added?.str ? validateCsv(added.str, item as any) : null
        });
      });  
  }
  return csvTypes.map((item) => {
    const added = state.added.find((i) => i.name == item.name);
    return Object.assign(item, {
      added,
      loading: added?.str === null,
      validate: added?.str ? validateCsv(added.str, item as any) : null
    });
  });
});

const downloadPreview = (item: TCsvType) => {
  downloadFile(item.fields.join(",") + "\n", `${item.title}.csv`, "text/csv");
};

const upload = async () => {
  state.loading = true;
  if (!tableData.value.every((item) => (item.added ? item.validate?.valid : true))) {
    useAlertsStore().addAlert("error", "Can't upload invalid file, fix issues before upload");
  } else {
    // upload files
    const form = new FormData();
    state.added.forEach((i) => {
      form.append("file[]", i.file, i.filename);
    });
    const res = await FetchService.fetchData("/csv-upload", "post", form);
    const alert = useAlertsStore();
    if (typeof res?.message === "string" && res.message) {
      res.message = [res?.message];
    }
    res?.message?.forEach?.((i) => alert.addAlert(res.status ? "success" : "error", i));
    if (res?.status) state.added = [];
  }
  state.loading = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;
@use "sass:color";
@use "sass:map";

.validation-status {
  user-select: none;
  background-color: $gray-50;
  padding: 10px 30px;
  margin-top: auto 0;
  border-radius: 4px;
  border: 1px solid $gray-200;
  width: fit-content;

  color: rgb(4, 231, 4);
  display: flex;
  align-items: center;
  gap: 10px;

  &.invalid {
    color: rgb(255, 0, 0);
  }
}

.error-status {
  color: rgb(255, 0, 0);
  font-size: 1.1rem;
  font-weight: 600;
}
.success-status {
  color: rgb(0, 255, 0);
  font-size: 1.1rem;
  font-weight: 600;
}

.icon-col {
  width: 0px;
}

.integration-con {
  margin-top: 10px;
}

.title {
  color: $primary;
  font-weight: 600;
  font-size: 1.25rem;
}

.file-select-con {
  width: 100%;
  padding: 8px 15px;
  border: 3px dashed $gray-400;
  background-color: $primary-50;

  display: flex;
  //   flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 600px;
  border-radius: 8px;

  &.drag-over {
    background-color: $primary-75;
  }

  &.loading-upload {
    background-color: color.scale($primary-50, $lightness: 2%);
  }
}

.file-validation {
  width: 100%;
  & > div:first-child {
    text-align: center;
    width: 100%;
  }

  .validation-status {
    user-select: none;
    background-color: $gray-50;
    padding: 10px;
    margin-top: 10px;
    margin-bottom: 10px;
    border-radius: 4px;
    border: 1px solid $gray-200;

    color: rgb(4, 231, 4);
    display: flex;
    align-items: center;
    gap: 10px;

    &.invalid {
      color: rgb(255, 0, 0);
    }
  }
}

.error-item {
  color: rgb(255, 0, 0);
}

.warning-item {
  color: #ff9200;
}
</style>
